import { ALLOWED_PLATFORMS, type Platform } from '@/constants/platforms';
import type { ValidationResult, PlatformService } from '@/types';

/**
 * Platform service for business logic related to platform handling
 */
class ExchangePlatformService implements PlatformService {
  /**
   * Validate that a platform is one of the allowed values
   * @param platform - The platform string to validate
   * @returns ValidationResult with isValid true/false and optional error message
   */
  validatePlatform(platform: string): ValidationResult {
    if (!platform || typeof platform !== 'string') {
      return { isValid: false, error: 'Platform must be a non-empty string' };
    }

    const trimmed = platform.trim().toLowerCase();

    if (trimmed.length === 0) {
      return { isValid: false, error: 'Platform cannot be empty or whitespace only' };
    }

    if (!ALLOWED_PLATFORMS.includes(trimmed as Platform)) {
      return {
        isValid: false,
        error: `Unsupported platform: ${platform}. Supported platforms: ${ALLOWED_PLATFORMS.join(', ')}`,
      };
    }

    return { isValid: true };
  }

  /**
   * Get all allowed platforms
   * @returns Array of all supported platform identifiers
   */
  getAllowedPlatforms(): readonly Platform[] {
    return ALLOWED_PLATFORMS;
  }

  /**
   * Check if a platform is supported without detailed error information
   * @param platform - The platform string to check
   * @returns true if platform is supported, false otherwise
   */
  isPlatformSupported(platform: string): boolean {
    return this.validatePlatform(platform).isValid;
  }

  /**
   * Normalize platform string to standard format (trim and lowercase)
   * @param platform - The platform string to normalize
   * @returns Normalized platform string
   */
  normalizePlatform(platform: string): string {
    return platform.trim().toLowerCase();
  }
}

/**
 * Singleton platform service instance
 */
export const exchangePlatformService = new ExchangePlatformService();
